import type { ReactNode, RefObject } from 'react'
import React, { createContext, useContext, useMemo, useRef, useState } from 'react'

import { onWebViewModalClose, onWebViewModalOpen } from '@fourthwall/utils'

import { useOutsideClick } from '../../hooks/useOutsideClick'

interface DropdownProviderProps {
  children: ReactNode
}

interface DropdownContextProps {
  ref: RefObject<HTMLDivElement>
  isOpen: boolean
  toggle: () => void
  open: () => void
  close: () => void
}

const DropdownContext = createContext<DropdownContextProps | undefined>(undefined)

export const DropdownProvider = ({ children }: DropdownProviderProps) => {
  const ref = useRef(null)
  const [isOpen, setIsOpen] = useState(false)

  const toggle: DropdownContextProps['toggle'] = () => {
    setIsOpen((prevIsOpen) => {
      if (prevIsOpen) {
        onWebViewModalClose()
      } else {
        onWebViewModalOpen()
      }

      return !prevIsOpen
    })
  }

  const open: DropdownContextProps['open'] = () => {
    setIsOpen(true)
    onWebViewModalOpen()
  }

  const close: DropdownContextProps['close'] = () => {
    setIsOpen(false)
    onWebViewModalClose()
  }

  useOutsideClick(ref, () => {
    close()
  })

  const value = useMemo(
    () => ({
      ref,
      isOpen,
      toggle,
      open,
      close,
    }),
    [ref, isOpen],
  )

  return <DropdownContext.Provider value={value}>{children}</DropdownContext.Provider>
}

export const useDropdown = () => {
  const context = useContext(DropdownContext)

  if (context === undefined) {
    throw new Error('useDropdownContext must be used within a DropdownProvider')
  }

  return context
}
